import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';
import { LOCAL_STORAGE } from '../../core/services/user-storage.service';
import { navigateTo } from '../../core/utils/reload';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface Organization {
  customer_id: string;
  name: string;
  role: string;
  database_name: string;
}

@Component({
  selector: 'app-organizations',
  standalone: true,
  imports: [
    CardComponent,
    CardContentComponent,
    FormsModule,
    ButtonComponent,
    IconComponent,
    ModalDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './organizations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './organizations.component.css',
})
export class OrganizationsComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private log = inject(LoggerService).getLogger('org-switch');
  private localStorage = inject(LOCAL_STORAGE);

  organizations = signal<Organization[]>([]);
  currentCustomerId = signal<string>('');
  loading = signal(true);
  error = signal<string | null>(null);

  // Delete confirmation modal state
  showDeleteModal = signal(false);
  orgToDelete = signal<Organization | null>(null);
  deleteConfirmationInput = signal('');

  async ngOnInit() {
    try {
      const token = this.authService.getToken();
      this.log.debug('Token retrieved:', token ? `${token.substring(0, 20)}...` : 'null');

      if (!token) {
        this.router.navigate(['/login']);
        return;
      }

      const payload = this.authService.decodeJwt(token);
      this.log.debug('JWT decoded:', {
        customer_id: payload.customer_id,
        email: payload.email,
        sub: payload.sub,
      });

      this.currentCustomerId.set(payload.customer_id);
      this.log.debug('Set currentCustomerId to:', payload.customer_id);

      // Fetch user's organizations from backend
      const response = await fetch(`${environment.apiUrl}/customers/my-organizations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      this.log.debug('Organizations fetched:', data.organizations);
      this.organizations.set(data.organizations || []);
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      this.loading.set(false);
    }
  }

  async switchOrganization(customerId: string) {
    this.log.info('Starting switch to customer_id:', customerId);

    try {
      this.loading.set(true);

      const token = this.authService.getToken();
      this.log.debug('Current token:', token ? `${token.substring(0, 20)}...` : 'null');

      if (!token) {
        this.router.navigate(['/login']);
        return;
      }

      const currentPayload = this.authService.decodeJwt(token);
      this.log.debug('Current JWT customer_id:', currentPayload.customer_id);

      // Request new JWT with different customer_id
      this.log.info('Requesting new JWT from backend...');
      const response = await fetch(`${environment.apiUrl}/customers/switch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      if (!response.ok) {
        throw new Error('Failed to switch organization');
      }

      const data = await response.json();
      this.log.info('Received new token from backend');

      const newPayload = this.authService.decodeJwt(data.token);
      this.log.info('New JWT customer_id:', newPayload.customer_id);

      // Store new token in localStorage (survives tab discarding)
      this.localStorage.setItem('auth_token', data.token);
      this.log.info('Token saved to localStorage, reloading page...');

      // Force full page reload to reinitialize database with new customer_id
      navigateTo('/', 'switch organization — reinitialize database');
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Failed to switch organization');
      this.loading.set(false);
    }
  }

  isCurrentOrg(customerId: string): boolean {
    return customerId === this.currentCustomerId();
  }

  showDeleteConfirmation(org: Organization, event: Event) {
    event.stopPropagation(); // Prevent switching when clicking delete
    this.orgToDelete.set(org);
    this.deleteConfirmationInput.set('');
    this.showDeleteModal.set(true);
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
    this.orgToDelete.set(null);
    this.deleteConfirmationInput.set('');
  }

  canConfirmDelete(): boolean {
    const org = this.orgToDelete();
    return org !== null && this.deleteConfirmationInput() === org.name;
  }

  async confirmDelete() {
    const org = this.orgToDelete();
    if (!org || !this.canConfirmDelete()) {
      return;
    }

    try {
      this.loading.set(true);
      this.showDeleteModal.set(false);

      const token = this.authService.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${environment.apiUrl}/customers/${org.customer_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to delete organization');
      }

      // Refresh the list
      await this.ngOnInit();
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Failed to delete organization');
    } finally {
      this.loading.set(false);
      this.orgToDelete.set(null);
      this.deleteConfirmationInput.set('');
    }
  }
}
